// ── Ops Center Mockup data model ─────────────────────────────────────────────

// Step types that appear in real Ops Center workflow JSON. MATCHLIST/KYC/IDV/AML/
// FRAUD are the configurable ones offered in the palette; DECISION and
// TOGGLEMONITORING also occur in imported workflows and must render safely.
export type StepName = 'MATCHLIST' | 'KYC' | 'IDV' | 'AML' | 'KYB' | 'FRAUD' | 'DECISION' | 'TOGGLEMONITORING'

export interface WorkflowVariable {
  name: string
  type: string            // e.g. 'STRING'
  default?: string
  description?: string
}

// ── Expression tree (branch conditions) ──
export interface SimpleExpr {
  type: 'SIMPLE'
  sourcePath: string      // e.g. "tasks.step_kyc_check_(dvs).stepResult.result"
  operator: 'EQ' | 'NEQ' | 'GT' | 'LT' | 'GTE' | 'LTE'
  value: string
}
export interface ComplexExpr {
  type: 'COMPLEX'
  and?: Expression[]
  or?: Expression[]
}
export type Expression = SimpleExpr | ComplexExpr

// ── Tasks (nested tree) ──
export interface StepConfiguration {
  type: 'STEP'
  name: StepName
  parameters?: Record<string, unknown>
}
export interface StepTask {
  id: string
  name: string
  description?: string
  configuration: StepConfiguration
}

export interface Branch {
  name: string
  expression?: Expression
  tasks: Task[]
}
export interface BranchConfiguration {
  type: 'BRANCH'
  branches: Branch[]
  defaultBranch?: unknown
}
export interface BranchTask {
  id: string
  name: string
  description?: string
  configuration: BranchConfiguration
}

export type Task = StepTask | BranchTask

export function isBranchTask(t: Task): t is BranchTask {
  return t.configuration.type === 'BRANCH'
}
export function isStepTask(t: Task): t is StepTask {
  return t.configuration.type === 'STEP'
}

// ── Webhook (synthetic terminal node) ──
export interface WebhookConfig {
  url: string
  headers?: Record<string, string>
  events?: string[]
  retryCount?: number
}

// ── Related config files (mock, per node) ──
export interface ConfigFile {
  name: string            // e.g. "idv_services"
  content: string         // editable mock content
}

// ── Workflow ──
export interface Workflow {
  id: string
  accountId: string
  name: string
  description?: string
  version: number
  tags: string[]
  variables: WorkflowVariable[]
  tasks: Task[]
  webhook: WebhookConfig
  defaultErrorHandling?: { ignoreErrors: boolean }
  // Per-node config-file contents, keyed by task id (synthetic ids too)
  configFiles?: Record<string, ConfigFile[]>
  createdAt: string
  updatedAt: string
}

// ── Recipe (v1 clients — entity_profiles.json) ──
// V1 clients don't use the v2 workflow engine; a "recipe" (verification profile)
// defines which check types run and in what order, driven by entity_profiles.json.
export interface Recipe {
  id: string
  name: string          // e.g. 'safe_harbour_id'
  longName: string      // e.g. 'Safe Harbour & ID'
  checkType: string[]   // e.g. ['blacklist','two_plus','id','pep','idvalidate']
  checkOrder: string    // e.g. 'blacklist, !id, ent, idv, aml'
  mkyc: boolean         // mKYC modifier enabled
  createdAt: string
  updatedAt: string
}

// ── Account ──
export interface Account {
  id: string
  name: string
  description?: string
  workflowIds: string[]
  createdAt: string
  updatedAt: string
}

// ── React Flow graph overlay ──
export type FlowNodeKind = 'START' | 'END' | 'WEBHOOK' | 'STEP' | 'BRANCH'
export interface FlowNodeData {
  kind: FlowNodeKind
  taskId?: string         // undefined for synthetic START/END/WEBHOOK
  label: string
  stepName?: StepName     // when kind === 'STEP'
  branchName?: string     // label of incoming branch edge, for context
  __task?: Task           // authoritative task, carried for lossless round-trip
}
